/**
 * I18N-BASE.JS - Sistema de Internacionalización Compartido
 *
 * SINGLE SOURCE OF TRUTH para:
 * - Traducciones de colores (colors)
 * - Traducciones de apuestas/grupos de receta (apartado `recetas`, i18n-recetas.js)
 * - Clase base LanguageManager
 *
 * ARQUITECTURA:
 * - Este archivo se carga PRIMERO en el build
 * - front-tv y front-customer extienden con sus traducciones específicas
 * - Evita duplicación de ~300 líneas de código
 *
 * IDIOMAS SOPORTADOS: es (español), en (inglés), pt (portugués), ru (ruso)
 */

// Traducciones de las CLAVES de las recetas (apartado separado, i18n-recetas.js).
// Se funde en I18N_BASE como sección `recetas` → `t('recetas.<clave>')`.
import { I18N_RECETAS } from './i18n-recetas.js';
import { escapeHtml } from '../../../utils/escape-html.js';
import { nombreI18nDePaletaDeApuesta } from '../../../lotowheel-render/lector-de-rueda.js';
import { obtenerProductoDelSorteoActivo } from './rueda-del-sorteo-activo.js';

/**
 * Rango de un array de sectores como "menor-mayor" (ej. [4,2,6,...,3] → "1-12").
 * Devuelve '' si está vacío. min/max porque los sectores vienen en orden de
 * RUEDA (no ascendente): el primer/último elemento no es el menor/mayor.
 */
function formatearRangoDeSectores(sectores) {
    if (!sectores || sectores.length === 0) return '';
    return `${Math.min(...sectores)}-${Math.max(...sectores)}`;
}

// Apartado i18n de las etiquetas visibles por id de línea (`lineas.<id>`,
// i18n-recetas.js) y grupo de la receta cuyas líneas se nombran por el tema.
const PREFIJO_DE_CLAVE_DE_LINEA = 'lineas.';
const GRUPO_DE_APUESTAS_DE_COLOR = 'color';

// ============================================================
// TRADUCCIONES BASE COMPARTIDAS
// ============================================================

export const I18N_BASE = {
    // Colores - Traducción dinámica según tema
    // Keys en inglés (estándar del tema) -> traducción al idioma actual
    colors: {
        es: {
            red: 'ROJO',
            black: 'NEGRO',
            blue: 'AZUL',
            gold: 'DORADO',
            green: 'VERDE',
            white: 'BLANCO',
            purple: 'MORADO',
            orange: 'NARANJA',
        },
        en: {
            red: 'RED',
            black: 'BLACK',
            blue: 'BLUE',
            gold: 'GOLD',
            green: 'GREEN',
            white: 'WHITE',
            purple: 'PURPLE',
            orange: 'ORANGE',
        },
        pt: {
            red: 'VERMELHO',
            black: 'PRETO',
            blue: 'AZUL',
            gold: 'DOURADO',
            green: 'VERDE',
            white: 'BRANCO',
            purple: 'ROXO',
            orange: 'LARANJA',
        },
        ru: {
            red: 'КРАСНОЕ',
            black: 'ЧЁРНОЕ',
            blue: 'СИНЕЕ',
            gold: 'ЗОЛОТОЕ',
            green: 'ЗЕЛЁНОЕ',
            white: 'БЕЛОЕ',
            purple: 'ФИОЛЕТОВОЕ',
            orange: 'ОРАНЖЕВОЕ',
        },
    },

    // (La tabla de premios usa el apartado `recetas` — i18n-recetas.js — donde la
    // clave es el `grupo`/id de la apuesta. El viejo `prizeTable` murió con el
    // modelo viejo: el runtime nunca lo consume.)

    // Traducciones del ticket impreso / preview — compartido entre front-customer y front-cashier
    tiquete: {
        es: {
            sorteo: 'SORTEO',
            jugado: 'JUGADO',
            apostado: 'Apostado',
            cobertura: 'COBERTURA',
            numeros: 'numeros',
            tablaPremios: 'TABLA DE PREMIOS',
            premiosDirectos: 'PREMIOS DIRECTOS',
            apuestasGrupo: 'APUESTAS DE GRUPO',
            yaIncluidas: 'Valido solo sin acierto directo.<br>(Si acertaste, ya esta sumado arriba).',
            siSale: 'SI SALE...',
            tuGanas: 'TU GANAS',
            premioMaximo: 'Premio Maximo Posible',
            id: 'ID',
            cancelar: 'Cancelar',
            confirmar: 'Confirmar',
            masGrupos: '+ {{count}} mas...',
            // Rango de sorteos cubiertos por un ticket multi-sorteo.
            // {{from}} = numero del primer sorteo, {{to}} = numero del ultimo.
            rangoSorteos: '#{{from}} al #{{to}}',
            patrones: {
                nums: '{{count}} nums',
            },
        },
        en: {
            sorteo: 'DRAW',
            jugado: 'PLAYED',
            apostado: 'Bet',
            cobertura: 'COVERAGE',
            numeros: 'numbers',
            tablaPremios: 'PRIZE TABLE',
            premiosDirectos: 'DIRECT PRIZES',
            apuestasGrupo: 'GROUP BETS',
            yaIncluidas: 'Valid only without direct hit.<br>(If you hit, already added above).',
            siSale: 'IF IT LANDS...',
            tuGanas: 'YOU WIN',
            premioMaximo: 'Maximum Possible Prize',
            id: 'ID',
            cancelar: 'Cancel',
            confirmar: 'Confirm',
            masGrupos: '+ {{count}} more...',
            rangoSorteos: '#{{from}} to #{{to}}',
            patrones: {
                nums: '{{count}} nums',
            },
        },
        pt: {
            sorteo: 'SORTEIO',
            jugado: 'JOGADO',
            apostado: 'Apostado',
            cobertura: 'COBERTURA',
            numeros: 'números',
            tablaPremios: 'TABELA DE PRÊMIOS',
            premiosDirectos: 'PRÊMIOS DIRETOS',
            apuestasGrupo: 'APOSTAS DE GRUPO',
            yaIncluidas: 'Válido apenas sem acerto direto.<br>(Se acertou, já está somado acima).',
            siSale: 'SE SAIR...',
            tuGanas: 'VOCÊ GANHA',
            premioMaximo: 'Prêmio Máximo Possível',
            id: 'ID',
            cancelar: 'Cancelar',
            confirmar: 'Confirmar',
            masGrupos: '+ {{count}} mais...',
            rangoSorteos: '#{{from}} ao #{{to}}',
            patrones: {
                nums: '{{count}} nums',
            },
        },
        ru: {
            sorteo: 'РОЗЫГРЫШ',
            jugado: 'СЫГРАНО',
            apostado: 'Ставка',
            cobertura: 'ПОКРЫТИЕ',
            numeros: 'номеров',
            tablaPremios: 'ТАБЛИЦА ПРИЗОВ',
            premiosDirectos: 'ПРЯМЫЕ ПРИЗЫ',
            apuestasGrupo: 'ГРУППОВЫЕ СТАВКИ',
            yaIncluidas: 'Действует только без прямого попадания.<br>(Если попали — уже посчитано выше).',
            siSale: 'ЕСЛИ ВЫПАДЕТ...',
            tuGanas: 'ВЫ ВЫИГРАЕТЕ',
            premioMaximo: 'Максимальный возможный приз',
            id: 'ID',
            cancelar: 'Отмена',
            confirmar: 'Подтвердить',
            masGrupos: '+ ещё {{count}}...',
            rangoSorteos: '#{{from}} — #{{to}}',
            patrones: {
                nums: '{{count}} номеров',
            },
        },
    },

    // Claves declaradas por las recetas (apartado separado i18n-recetas.js).
    ...I18N_RECETAS,
};

// ============================================================
// LANGUAGE MANAGER - Clase Base
// ============================================================

/**
 * Clase base para gestión de idiomas.
 * Los frontends pueden extender o usar directamente.
 *
 * @param {string} storageKey - Clave para localStorage
 * @param {string} defaultLang - Idioma por defecto
 * @param {Object} languages - Traducciones del frontend (LANGUAGES)
 */
export class LanguageManagerBase {
    constructor(storageKey, defaultLang = 'es', languages = null) {
        // storageKey obligatorio: cada front pasa su key ya prefijada por brand
        // (ver storage-namespace.js → keyDash). Default literal eliminado para
        // evitar que un consumidor olvidado escriba en `loto37-language` y
        // contamine localStorage de otra brand.
        if (!storageKey || typeof storageKey !== 'string') {
            throw new Error('[LanguageManagerBase] storageKey obligatorio (string).');
        }
        this.storageKey = storageKey;
        this.defaultLang = defaultLang;
        this._languages = languages; // Traducciones del frontend inyectadas
        this.currentLanguage = this._loadLanguage();
    }

    _loadLanguage() {
        // try/catch: localStorage puede EXISTIR y aun así lanzar al usarse
        // (storage bloqueado en iframe de terceros — caso B2B embebido — o
        // Node con webstorage sin --localstorage-file). La preferencia
        // degrada al default; no es camino primario.
        try {
            if (typeof localStorage === 'undefined') return this.defaultLang;
            const saved = localStorage.getItem(this.storageKey);
            return saved && this._isValidLanguage(saved) ? saved : this.defaultLang;
        } catch (_) {
            return this.defaultLang;
        }
    }

    _isValidLanguage(lang) {
        // Verificar si el idioma existe en I18N_BASE o en las traducciones inyectadas
        return I18N_BASE.colors[lang] !== undefined || (this._languages && this._languages[lang] !== undefined);
    }

    getCurrentLanguage() {
        return this.currentLanguage;
    }

    setLanguage(langCode) {
        if (!this._isValidLanguage(langCode)) return false;
        this.currentLanguage = langCode;
        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(this.storageKey, langCode);
            }
        } catch (_) {
            /* storage bloqueado: la preferencia vive solo en memoria */
        }
        return true;
    }

    /**
     * Aplica el idioma por defecto del operador (brand) SOLO si el usuario
     * no eligió antes uno. La preferencia explícita del usuario en localStorage
     * siempre gana — este método nunca la pisa.
     *
     * Llamado por el front tras recibir el branding del backend (endpoint
     * /api/public/branding). Si el brand declara `defaultLanguage: 'pt'`, en
     * Brasil arranca en portugués; el usuario puede cambiarlo y queda guardado.
     *
     * @param {string} langCode - código corto ('es', 'en', 'pt', 'ru')
     * @returns {boolean} true si efectivamente aplicó el cambio
     */
    aplicarDefaultDeBrandSiNoHayPreferencia(langCode) {
        if (!langCode || !this._isValidLanguage(langCode)) return false;
        // Si el usuario ya eligió antes, no pisar.
        try {
            if (typeof localStorage !== 'undefined' && localStorage.getItem(this.storageKey)) {
                return false;
            }
        } catch (_) {
            /* storage bloqueado: no hay preferencia persistida que respetar */
        }
        // Idéntico al actual — no hace falta repintar.
        if (this.currentLanguage === langCode) return false;
        this.currentLanguage = langCode;
        return true;
    }

    /**
     * Obtener traducción con notación de punto.
     * Busca primero en LANGUAGES (frontend), luego en I18N_BASE (compartido).
     *
     * @param {string} path - Ruta de la traducción (ej: 'colors.red', 'recetas.aciertoDirecto')
     * @param {Object} params - Parámetros para reemplazar {{variable}}
     * @returns {string} Traducción o [path] si no existe
     */
    _traducir(path, params, escaparParametro) {
        const lang = this.currentLanguage;
        const keys = path.split('.');
        let value;

        // 1. Buscar en traducciones del frontend (inyectadas en constructor)
        if (this._languages && this._languages[lang]) {
            value = this._resolve(this._languages[lang], keys);
        }

        // 2. Si no encontró, buscar en I18N_BASE (compartido)
        if (value === undefined) {
            const section = keys[0]; // 'colors', 'recetas', 'betting'...
            const key = keys.slice(1).join('.');

            if (I18N_BASE[section]?.[lang]) {
                value = this._resolve(I18N_BASE[section][lang], key.split('.'));
            }
        }

        // 3. Fallback a español si no existe en el idioma actual
        if (value === undefined && lang !== 'es') {
            if (this._languages && this._languages.es) {
                value = this._resolve(this._languages.es, keys);
            }
            if (value === undefined) {
                const section = keys[0];
                const key = keys.slice(1).join('.');
                if (I18N_BASE[section]?.es) {
                    value = this._resolve(I18N_BASE[section].es, key.split('.'));
                }
            }
        }

        // 4. Si no encontró nada, retornar placeholder visible
        if (value === undefined) {
            console.warn(`[i18n] Translation not found: ${path}`);
            return `[${path}]`;
        }

        // 5. Reemplazar parámetros {{variable}}, escapando o no segun el
        //    CONTRATO del llamador (ver `t` y `tHtml` abajo).
        if (typeof value === 'string') {
            return value.replace(/\{\{(\w+)\}\}/g, (match, key) => {
                if (params[key] === undefined) return match;
                return escaparParametro ? escapeHtml(params[key]) : params[key];
            });
        }

        return value;
    }

    /**
     * TEXTO PLANO. El resultado va a `textContent`, a un toast, a un mensaje
     * de WhatsApp o a cualquier destino que muestre caracteres tal cual.
     *
     * Los parámetros NO se escapan: acá escapar seria un BUG visible — un
     * negocio llamado «Bar & Lotería» llegaría al WhatsApp del cliente como
     * «Bar &amp; Lotería».
     *
     * @param {string} path
     * @param {object} [params]
     * @returns {string} texto para mostrar como texto.
     */
    t(path, params = {}) {
        return this._traducir(path, params, false);
    }

    /**
     * PLANTILLA HTML. El resultado va a `innerHTML`, así que sus parámetros
     * se ESCAPAN y la plantilla no.
     *
     * Es la distinción que justifica tener dos funciones: la plantilla la
     * escribimos nosotros y usa `<strong>`/`<br>` a propósito, mientras que el
     * parámetro trae datos ajenos — el nombre de una terminal cargado desde el
     * admin, un código que un tercero imprimió en un QR. Sin escapar, ese
     * parámetro convierte un mensaje en marcado ejecutable.
     *
     * El contrato lo elige QUIEN LLAMA, porque solo él sabe a dónde va el
     * resultado. Meter esa decisión dentro de la traducción —como estaba—
     * rompe el otro destino: `t()` escapando siempre mostraba entidades
     * literales en toasts y WhatsApp.
     *
     * @param {string} path
     * @param {object} [params] - datos ajenos; se escapan.
     * @returns {string} HTML seguro para `innerHTML`.
     */
    tHtml(path, params = {}) {
        return this._traducir(path, params, true);
    }

    /**
     * Resolver path en objeto anidado
     */
    _resolve(obj, keys) {
        let value = obj;
        for (const key of keys) {
            if (value === undefined || value === null) return undefined;
            // Normalizar a minúsculas para tolerancia
            const normalizedKey = key.toLowerCase();
            value = value[normalizedKey] ?? value[key];
        }
        return value;
    }

    /**
     * Traducir color por key del tema.
     * SINGLE SOURCE OF TRUTH - usa I18N_BASE.colors
     *
     * @param {string} colorKey - Key del color (ej: 'orange', 'red')
     * @returns {string} Nombre traducido o [colors.key] si falla
     */
    translateColor(colorKey) {
        if (!colorKey) return '[no-color-key]';
        const key = colorKey.toLowerCase();
        return this.t(`colors.${key}`);
    }

    /**
     * Traducir con inyeccion de valores del PRODUCTO (receta + tema). Los
     * valores dinamicos se derivan de la receta del producto; las lineas de
     * color se nombran segun su tema.
     *
     * Templates soportados:
     * - {{range}}: "0-36" o "0, 00, 1-36"
     * - {{blockKeys}}: "A-F" o "A-B"
     * - {{mirrorPairs}}: "12-21 / 13-31 / 23-32"
     * - {{twinNumbers}}: "11 / 22 / 33"
     * - {{lowRange}}: "1-18"
     * - {{highRange}}: "19-36"
     * - {{thirdRanges}}: "1-12 / 13-24 / 25-36"
     *
     * @param {string} path - Ruta de la traduccion
     * @param {Object} extraParams - Parametros adicionales
     * @param {Object|null} [producto] - producto de `crearProductoDeRueda`. Lo
     *   pasa quien traduce OTRO producto que el activo (la caja con varios
     *   sorteos en pantalla, el historial del lobby). Sin él: el producto del
     *   sorteo activo, que lanza si el front no lo fijó (bug de orden).
     * @returns {string} Traduccion con valores inyectados
     */
    tWithConfig(path, extraParams = {}, producto = null) {
        const { receta, tema } = producto ?? obtenerProductoDelSorteoActivo();
        // Líneas de COLOR: el id es estable (money-path) pero el nombre visible
        // lo dicta el TEMA del producto — 'rojos' en HALLOWEEN se pinta naranja
        // y dice NARANJA; en BRASILEIRO, DORADO. Es el criterio que ya aplicaba
        // la tabla de premios, en el punto por el que pasan TODAS las etiquetas
        // de línea (mat, stats, tiquete, historial, celebración, TV). Sin tema
        // (el sorteo no lo declara) → diccionario.
        if (tema !== null) {
            const nombrePorTema = this._nombreDeLineaDeColorSegunTema(path, receta, tema);
            if (nombrePorTema !== null) return nombrePorTema;
        }
        // DECLARATIVO: todo se deriva de la RECETA por GRUPO de apuesta. Una
        // receta nueva con un grupo arbitrario (ej. `'pais'`) queda soportada sin
        // tocar este archivo — los rangos/labels se calculan de sus `sectores`.
        const fijas = receta.apuestas.filter(a => Array.isArray(a.sectores));
        const delGrupo = grupo => fijas.filter(a => a.grupo === grupo);
        const sectoresDeGrupoEnOrden = grupo => delGrupo(grupo).map(a => a.sectores);
        // mitad: primera apuesta = "baja", segunda = "alta" (orden de la receta).
        const apuestasDeMitad = delGrupo('mitad');
        const bajos = apuestasDeMitad[0] ? apuestasDeMitad[0].sectores : [];
        const altos = apuestasDeMitad[1] ? apuestasDeMitad[1].sectores : [];
        const docenas = sectoresDeGrupoEnOrden('tercios');
        const columnas = delGrupo('columnas');
        // Bloques: traducir cada id por su label corto en `lineas.<id>` (A, B, C…).
        // La receta usa ids semanticos (`bloqueA`) pero la UI quiere "A".
        // Fail-fast: si falta `lineas.<id>` en el idioma actual, la receta esta
        // mal declarada — no queremos mostrar el id crudo ni `[lineas.xxx]`.
        // Bloques: traducir cada id por su label corto en `lineas.<id>` (A, B, C…).
        // La receta usa ids semanticos (`bloqueA`) pero la UI quiere "A".
        // `t()` ya consulta I18N_BASE/I18N_RECETAS (SSOT de lineas.*) y si falta
        // devuelve `[lineas.xxx]` con console.warn — detectamos ese placeholder
        // para tirar fail-fast en vez de pintar la basura.
        const bloques = delGrupo('bloques').map(a => {
            const path = `lineas.${a.id}`;
            const label = this.t(path);
            if (label === `[${path}]`) {
                throw new Error(
                    `[i18n-recetas] Falta ${path} en idioma '${this.currentLanguage}' — ` +
                        `la receta declara un bloque con id '${a.id}' pero i18n-recetas no tiene su label corto.`
                );
            }
            return label;
        });
        const espejos = sectoresDeGrupoEnOrden('espejos');
        // Gemelos: union de todas las apuestas grupo='gemelos' (suele ser una sola).
        const gemelos = delGrupo('gemelos').reduce((acc, a) => acc.concat(a.sectores), []);
        // Acierto directo: apuesta geometrica de aridad 1 (o fija de 1 sector).
        const patronPleno = receta.apuestas.find(
            p => p.aridad === 1 || (Array.isArray(p.sectores) && p.sectores.length === 1 && p.grupo === 'directo')
        );
        // Total de sectores de la rueda, ceros incluidos — la misma cuenta que
        // la factory expone como `cantidadTotalDeSectoresDeLaRueda`.
        const totalSectores = receta.sectores.length;

        const configParams = {
            // El "0-N" se sobreentiende que incluye todos los ceros.
            range: `0-${totalSectores}`,
            blockKeys: bloques.join('-'),
            mirrorPairs: espejos.map(([a, b]) => `${a}/${b}`).join(', '),
            twinNumbers: gemelos.join(' / '),
            twinNumbersCompact: gemelos.join('/'),
            // min/max del conjunto: los sectores vienen en orden de rueda (no
            // ascendente), así que el primer/último elemento del array no es el
            // menor/mayor. Ej. rueda10 bajos=[1,3,5,2,4] → "1-5", no "1-4".
            lowRange: bajos.length ? `${Math.min(...bajos)}-${Math.max(...bajos)}` : '',
            highRange: altos.length ? `${Math.min(...altos)}-${Math.max(...altos)}` : '',
            thirdRanges: docenas.map(formatearRangoDeSectores).join(' / '),
            third1Range: formatearRangoDeSectores(docenas[0]),
            third2Range: formatearRangoDeSectores(docenas[1]),
            third3Range: formatearRangoDeSectores(docenas[2]),
            columnLabels: columnas.map((_, i) => `${i + 1}a`).join(' / '),
            totalNumbers: totalSectores,
            maxNumber: totalSectores,
            multiplierPleno: patronPleno ? patronPleno.multiplicador : 0,
            ...extraParams,
        };

        return this.t(path, configParams);
    }

    /**
     * Nombre visible de una línea de color (`lineas.<id>`) según el tema del
     * producto, o `null` si la clave no es de una apuesta del grupo color de
     * la receta o el tema no nombra su paleta. El criterio es el GRUPO de la
     * apuesta, no la paleta de su primer sector: 'pares' arranca en un sector
     * rojo y no por eso se llama ROJO.
     */
    _nombreDeLineaDeColorSegunTema(path, receta, tema) {
        if (!path.startsWith(PREFIJO_DE_CLAVE_DE_LINEA)) return null;
        const idDeLinea = path.slice(PREFIJO_DE_CLAVE_DE_LINEA.length);
        const apuestaDeColor = receta.apuestas.find(a => a.id === idDeLinea && a.grupo === GRUPO_DE_APUESTAS_DE_COLOR);
        if (!apuestaDeColor) return null;
        const claveDeColor = nombreI18nDePaletaDeApuesta(apuestaDeColor, receta, tema);
        return claveDeColor ? this.translateColor(claveDeColor) : null;
    }

    /**
     * Obtener lista de idiomas disponibles
     */
    getAvailableLanguages() {
        const langs = [];
        const codes = ['es', 'en', 'pt', 'ru'];

        for (const code of codes) {
            if (this._languages && this._languages[code]) {
                langs.push({
                    code,
                    name: this._languages[code].name || code.toUpperCase(),
                    flag: this._languages[code].flag || '',
                });
            }
        }

        return langs;
    }
}

// ============================================================
// NAMESPACE EXPORT
// ============================================================

export const I18nBase = {
    I18N_BASE,
    LanguageManagerBase,
};

export default I18nBase;

// ============================================================
// BACKWARD COMPATIBILITY
// ============================================================

// Browser global
if (typeof window !== 'undefined') {
    window.I18N_BASE = I18N_BASE;
    window.LanguageManagerBase = LanguageManagerBase;
}

// CommonJS
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { I18N_BASE, LanguageManagerBase };
}
